/**
 * ProcessPane — the workbench's process-view primitive.
 *
 * Binds the `ProcessMode` view (Flow / Sequence / Tree + scrubber) to the
 * workspace services: the model comes from the shared compile
 * (WorkspaceModelProvider), the active process from the store's selection
 * slice, and ALL of the playback state (walker, branches, scrubber, auto-play,
 * swimlane context) from the `ProcessPlaybackProvider` — the same service the
 * board's step decorations read. The viewer mounts it in process mode in place
 * of its old prop-wired ProcessMode.
 *
 * `onSelectModule` stays a prop — jumping from a process step to a module is
 * shell orchestration (cross-mode navigation), via the `onArchlangNav` prop.
 */
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { ProcessMode } from "../../process/ProcessMode.js";
import { TestsPanel } from "../../process/TestsPanel.js";
import { SaveAsTestButton } from "../../process/SaveAsTestButton.js";
import { StepInjectPanel } from "../../process/StepInjectPanel.js";
import { useProcessTests } from "../../process/useProcessTests.js";
import { applyTestPins } from "../../process/applyTestPins.js";
import type { TestDecl, NodeId } from "@archlang/engine";
import { useProcessKeyboard } from "../../process/useProcessKeyboard.js";
import {
  useProcessSelection,
  orderedStepIds,
} from "../../process/processSelection.js";
import { buildProcessCommands } from "../../process/processCommands.js";
import { useRegisterCommandSource } from "../plugins/commandSources.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceSelection, useActiveFile } from "../WorkspaceStore.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { useModelMutationsOptional } from "../mutations.js";
import { makeBpmnDraftLaneEditApi } from "../../process/bpmnLaneEdit.js";
import { makeProcessStepEditApi } from "../../process/processStepEdit.js";
import { useProcessLaneDraft } from "../../process/useProcessLaneDraft.js";
import { parseDraftLaneBody } from "../../process/processLaneDraft.js";
import { flowBodyRowText } from "../../views/clauseModel.js";
import { buildNodeUniverse } from "@archlang/engine";
import { useProcessPlaybackContext } from "../ProcessPlaybackContext.js";
import { useAspectOverlay } from "../../hooks/useAspectOverlay.js";
import { useProcessLatencyBadge } from "../../process/useProcessLatencyBadge.js";
import { useProcessStepLatencies } from "../../process/useProcessStepLatencies.js";
import { useProcessEnds } from "../../process/useProcessEnds.js";
import {
  buildAspectColoring,
  buildTypeColoring,
  ColorLegend,
  NodeColoringProvider,
  type NodeColoring,
} from "../../board/coloring/index.js";
import { useProcessReorder } from "../../board/telemetry/useProcessReorder.js";
import { AspectControls } from "./AspectControls.js";
import { EditToggle } from "./EditToggle.js";
import { ProcessUndoButton } from "./ProcessUndoButton.js";
import { useOptionalEditMode } from "../EditModeContext.js";
import { IconButton, useToastOptional } from "@archlang/ui";

export interface ProcessPaneProps {
  /** Jump from a process step / lane to its module (cross-mode navigation). */
  readonly onSelectModule: (qualified: string) => void;
}

export function ProcessPane({ onSelectModule }: ProcessPaneProps): ReactElement {
  const store = useWorkspace();
  const toast = useToastOptional();
  const { model, resolvedModel, processModels, compiledSources } = useWorkspaceModel();
  const { activeProcessId } = useWorkspaceSelection(store);
  const latencyBadge = useProcessLatencyBadge(resolvedModel, processModels, activeProcessId);
  const playback = useProcessPlaybackContext();
  const stepLatencies = useProcessStepLatencies(
    resolvedModel,
    processModels,
    activeProcessId,
    playback.walkResultAllBranches ?? playback.walkResult,
  );
  // Live process-conformance: steps observed running out of order vs the model.
  const reorder = useProcessReorder(playback.walkResult?.steps);
  const processEndsList = useProcessEnds(resolvedModel, processModels, activeProcessId);
  // Same "colour by" source the board pane uses — one picker (AspectControls)
  // drives both surfaces: default = type colours, or the selected label key.
  // The BPMN view colours task owners from it.
  const aspect = useAspectOverlay(store, model);
  const { aspectColorKey, legendPinnedValue, setLegendHoverValue, setLegendPinnedValue } = aspect;
  const nodeColoring = useMemo<NodeColoring | null>(
    () =>
      model
        ? aspectColorKey
          ? buildAspectColoring(model.modules, aspectColorKey)
          : buildTypeColoring(model.modules)
        : null,
    [model, aspectColorKey],
  );
  // Process-tests (P7): per-process pass/fail/coverage; clicking a non-passing
  // test replays its pins into the scrubber (answered trace).
  const processTests = useProcessTests(resolvedModel, playback.activeProcessModel ?? null);
  const onRunTest = useCallback(
    (decl: TestDecl) => {
      if (resolvedModel && playback.activeProcessModel) {
        applyTestPins(decl, playback.activeProcessModel, resolvedModel, playback);
      }
    },
    [resolvedModel, playback],
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Per-view edit stance — gates the WYSIWYG step affordances (view mode keeps
  // clicks navigate/select). The toggle only appears when editing is possible.
  const editMode = useOptionalEditMode();

  // BPMN editing on the process viewer. The process diagram is a synthesized,
  // EDITABLE view (like the board): its bpmn lanes/pools are an ephemeral draft
  // edited in-browser to reason about hand-offs — nothing is written to the
  // model until "Save as view" promotes the draft to a durable view. Step→lane
  // drag is the one exception: reassigning a step's OWNER is process truth, a
  // real model edit.
  const mutations = useModelMutationsOptional();
  const activeFile = useActiveFile(store);
  const laneDraft = useProcessLaneDraft(activeProcessId ?? null);
  // Parsed draft body (for the layout post-pass) + the node universe the lane
  // selectors resolve against (memoized on the model, not per keystroke).
  const laneDraftBody = useMemo(() => parseDraftLaneBody(laneDraft.rows), [laneDraft.rows]);
  const laneUniverse = useMemo(
    () => (resolvedModel ? buildNodeUniverse(resolvedModel) : null),
    [resolvedModel],
  );
  const bpmnEdit = useMemo(() => {
    if (!activeProcessId) return undefined;
    const walk = playback.walkResultAllBranches ?? playback.walkResult;
    return makeBpmnDraftLaneEditApi({
      rows: laneDraft.rows,
      setRows: laneDraft.setRows,
      ...(mutations ? { mutations } : {}),
      ...(walk ? { stepByIndex: walk.byIndex, reassignTargets: walk.reassignTargets } : {}),
      processHandle: `#${activeProcessId}`,
    });
  }, [
    activeProcessId,
    laneDraft.rows,
    laneDraft.setRows,
    mutations,
    playback.walkResultAllBranches,
    playback.walkResult,
  ]);
  // Shared step-edit API — one edit path for all four surfaces (tree spine,
  // flow/sequence, BPMN). Owner reassignment reuses the walk's reassign
  // resolver; node ops go straight to mutateProcessStep.
  const stepEdit = useMemo(
    () =>
      makeProcessStepEditApi({
        mutations: mutations ?? null,
        processHandle: activeProcessId ? `#${activeProcessId}` : null,
        walk: playback.walkResultAllBranches ?? playback.walkResult,
        onReject: toast ? (msg) => toast.error(msg) : undefined,
      }),
    [mutations, activeProcessId, playback.walkResultAllBranches, playback.walkResult, toast],
  );
  // SHARED step selection (P5 #34) — ONE selection across the tree, the
  // flow/sequence board, and the keyboard below. ProcessMode threads it into
  // both surfaces + renders the floating toolbar; the keyboard reads it here.
  const selection = useProcessSelection();
  const selectionWalk = playback.walkResultAllBranches ?? playback.walkResult;
  const selectionOrder = useMemo(() => orderedStepIds(selectionWalk), [selectionWalk]);
  // Keyboard accelerators (P4/P5 #34): Delete removes the SELECTED steps (any
  // surface's selection) — falling back to the scrubber's active step when the
  // multi-selection is empty; Escape clears the selection (then the scrubber).
  const activeNodeId = useMemo(() => {
    const i = playback.activeStepIndex;
    if (i === null || i === undefined) return null;
    const nid = (playback.walkResult ?? playback.walkResultAllBranches)?.byIndex.get(i)?.nodeId;
    return nid ? String(nid) : null;
  }, [playback.activeStepIndex, playback.walkResult, playback.walkResultAllBranches]);
  // The nodeId the keyboard acts on: the last-selected step (document order) if
  // any, else the scrubber's active step — so Delete works in edit mode even
  // when playback has no active index.
  const keyboardNodeId = useMemo(() => {
    for (let i = selectionOrder.length - 1; i >= 0; i--) {
      const id = selectionOrder[i]!;
      if (selection.has(id)) return String(id);
    }
    return activeNodeId;
  }, [selectionOrder, selection, activeNodeId]);
  useProcessKeyboard({
    enabled: stepEdit.canEdit,
    activeNodeId: keyboardNodeId,
    onDelete: () => {
      // Batch-delete the whole selection when present (one journaled mutation);
      // otherwise the single scrubber-active step. Clear the selection on a
      // successful batch so the toolbar dismisses.
      const ids = selectionOrder.filter((id) => selection.has(id));
      if (ids.length > 0) {
        if (stepEdit.deleteSteps(ids)) selection.clear();
      } else if (keyboardNodeId) {
        stepEdit.deleteStep(keyboardNodeId as unknown as NodeId);
      }
    },
    onDeselect: () => {
      selection.clear();
      playback.setActiveStepIndex(null);
    },
  });
  // ⌘K command palette: the process step-edit commands are the keyboard-driven
  // mirror of the step-edit menu (shared step-edit api / mutation journal on the
  // active step). Folded onto the ONE root CommandPaletteHost (⌘K) as a command
  // SOURCE — buildProcessCommands returns [] outside edit mode, so the global
  // palette shows process commands only when editing is possible.
  const paletteContext = useMemo(
    () => ({
      api: stepEdit,
      activeNodeId,
      undo: () => mutations?.undo(),
      undoDepth: mutations?.undoDepth() ?? 0,
    }),
    [stepEdit, activeNodeId, mutations],
  );
  useRegisterCommandSource(useCallback(() => buildProcessCommands(paletteContext), [paletteContext]));
  const onCreateBpmnView = useMemo(() => {
    const proc = playback.activeProcess;
    if (!mutations || !proc) return undefined;
    return () => {
      const procRef = [...(proc.ownerPath ?? []), proc.name].filter(Boolean).join(".");
      // Target file: the active .arch, else the first .arch in the workspace
      // (§3.4, same rule the view builder's save uses).
      const target =
        activeFile && activeFile.endsWith(".arch")
          ? activeFile.startsWith("/")
            ? activeFile
            : "/" + activeFile
          : [...(compiledSources?.keys() ?? [])].find((k) => k.endsWith(".arch"));
      if (!target) return;
      // A valid identifier view name derived from the process, de-duplicated.
      const base = (proc.name.replace(/[^\w$]/g, "") || "Process") + "Bpmn";
      const taken = new Set((resolvedModel?.views ?? []).map((v) => v.name));
      let name = base;
      for (let i = 2; taken.has(name); i++) name = `${base}${i}`;
      // Promote the ephemeral lane draft (its complete rows) into the saved view.
      const laneLines = laneDraft.rows.map(flowBodyRowText).filter((t): t is string => t !== null);
      const flowClause = laneLines.length
        ? `flow ${procRef} bpmn { ${laneLines.join("; ")} }`
        : `flow ${procRef} bpmn`;
      const r = mutations.scaffoldView({ absFilePath: target, name, clauses: [flowClause] });
      if (r.status === "applied") store.openTab({ kind: "view", viewName: name });
    };
  }, [mutations, playback.activeProcess, activeFile, compiledSources, resolvedModel, store, laneDraft.rows]);

  // The viewer only mounts process mode past its own model guard; the null
  // branch is a type belt — render nothing rather than crash.
  if (!model) return <></>;
  return (
    <NodeColoringProvider value={nodeColoring}>
    <div className="flex h-full min-w-0 flex-1">
    <ProcessMode
      model={model}
      activeProcessId={activeProcessId}
      activeProcess={playback.activeProcess}
      walkResult={playback.walkResult}
      walkResultAllBranches={playback.walkResultAllBranches}
      walkerState={playback.activeWalkerState}
      activeStepIndex={playback.activeStepIndex}
      autoPlay={playback.autoPlay}
      setAutoPlay={playback.setAutoPlay}
      onAutoAdvance={playback.advanceStep}
      onSelectProcess={playback.handleSelectProcess}
      onSelectStepIndex={playback.setActiveStepIndex}
      onBranchSelect={(nodeId, label) => playback.selectBranch(nodeId as string, label)}
      onSubprocessToggle={(nodeId, expanded) =>
        playback.toggleSubprocess(nodeId as string, expanded)
      }
      onSelectModule={onSelectModule}
      bpmnEdit={bpmnEdit}
      stepEdit={stepEdit}
      selection={selection}
      editMode={editMode}
      editToggle={
        <>
          <ProcessUndoButton mutations={mutations ?? null} />
          <EditToggle canEdit={stepEdit.canEdit} />
        </>
      }
      laneDraftBody={laneDraftBody}
      laneUniverse={laneUniverse}
      onCreateBpmnView={onCreateBpmnView}
      swimlaneContext={playback.swimlaneContext}
      onGateAdvance={playback.gateAdvance}
      pendingChoice={playback.pendingChoice}
      onChoosePath={playback.choosePath}
      onDismissChoice={playback.dismissChoice}
      colorControls={<AspectControls colorOnly />}
      latencyBadge={latencyBadge}
      stepLatencies={stepLatencies}
      reorderedSteps={reorder.reorderedSteps}
      reorderedTraces={reorder.reorderedTraces}
      conformingTraces={reorder.conformingTraces}
      ends={processEndsList}
      legend={
        nodeColoring ? (
          <ColorLegend
            title={nodeColoring.title}
            entries={nodeColoring.legend}
            pinnedId={legendPinnedValue}
            onHover={setLegendHoverValue}
            onPin={(id) => setLegendPinnedValue((v) => (v === id ? null : id))}
          />
        ) : undefined
      }
    />
    {playback.activeProcessModel && resolvedModel && activeProcessId ? (
      sidebarOpen ? (
        <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-l border-white/[0.08]">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
            <span className="text-xs font-medium text-white/50">Tests</span>
            <IconButton
              variant="ghost"
              size="xs"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close test sidebar"
            >
              ×
            </IconButton>
          </div>
          <div className="flex flex-col gap-3 border-b border-white/[0.06] p-3">
            <StepInjectPanel
              walkResult={playback.walkResult}
              stepIndex={playback.activeStepIndex}
              state={playback.activeWalkerState}
              setSelectRuns={playback.setSelectRuns}
              setFailure={playback.setFailure}
              setDelay={playback.setDelay}
            />
            <SaveAsTestButton
              process={playback.activeProcessModel}
              processId={activeProcessId}
              model={resolvedModel}
              state={playback.activeWalkerState}
            />
          </div>
          {processTests.entries.length > 0 ? <TestsPanel tests={processTests} onRunTest={onRunTest} /> : null}
        </aside>
      ) : (
        <div className="flex shrink-0 flex-col border-l border-white/[0.08]">
          <IconButton
            variant="ghost"
            size="md"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open test sidebar"
            title="Tests"
          >
            ⚗
          </IconButton>
        </div>
      )
    ) : null}
    </div>
    </NodeColoringProvider>
  );
}
